import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Divider,
  Avatar,
  Chip,
  CircularProgress,
  Stack,
  IconButton,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FolderIcon from '@mui/icons-material/Folder';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import SpaIcon from '@mui/icons-material/Spa';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AssignmentIcon from '@mui/icons-material/Assignment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import EventNoteIcon from '@mui/icons-material/EventNote';
import axios from 'axios';
import { toast } from 'react-toastify';

// Importation des sous-onglets
import OverviewTab from '../tabs/OverviewTab';
import HealthTab from '../tabs/HealthTab';
import LifePathTab from '../tabs/LifePathTab';
import LifestyleTab from '../tabs/LifestyleTab';
import SoaTab from '../tabs/SoaTab';
import CaregiversTab from '../tabs/CaregiversTab';
import DocumentsTab from '../tabs/DocumentsTab';
import MetaInfoTab from '../tabs/MetaInfoTab';
import ClientAccountTab from '../tabs/ClientAccountTab';
import ReservationTab from '../tabs/ReservationTab';
import { COLORS, isFilled } from '../ViewPatientDialog.utils';
import { useAuth } from '../../../contexts/AuthContext';

const TabPanel = ({ children, value, index }) => {
  if (value !== index) return null;
  return (
    <Box role="tabpanel" sx={{ pt: 3 }}>
      {children}
    </Box>
  );
};

// Composant Tableau des Comptes Rendus du Patient
const ComptesRendusPatientTab = ({ patient, apiUrl, rolePath, token }) => {
  const [comptesRendus, setComptesRendus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCr, setSelectedCr] = useState(null);

  useEffect(() => {
    if (!patient?.id_patient_profile) return;

    const fetchComptesRendus = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${apiUrl}/${rolePath}/comptes-rendus/g/all`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        });

        const allData = res.data?.data || [];
        const filtered = allData.filter(
          (cr) => (cr.id_patient_profile || cr.patient_profile?.id_patient_profile) === patient.id_patient_profile
        );

        setComptesRendus(filtered);
      } catch (err) {
        console.error('Erreur chargement comptes rendus:', err?.response?.data || err);
        toast.error('Erreur lors du chargement des comptes rendus');
      } finally {
        setLoading(false);
      }
    };

    fetchComptesRendus();
  }, [patient, apiUrl, rolePath, token]);

  const handleDownloadPDF = (report) => {
    if (!report) return;
    const printWindow = window.open('', '_blank');

    const infoOffre = report.reservation?.offre?.info_offre || {};
    const offreTitre = infoOffre.titre_offre || report.pratique || 'Prestation SOA';
    const offreLieu = infoOffre.address_ws ? `${infoOffre.address_ws}, ${infoOffre.city_ws}` : 'Locaux S.O.A';

    const praticien = report.user || {};
    const praticienNom = [praticien.prenom_user, praticien.nom_user].filter(Boolean).join(' ') || 'Praticien SOA';

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="fr">
      <head>
        <meta charset="UTF-8">
        <title>Compte Rendu - ${report.numero_seance || 'Séance'}</title>
        <style>
          body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #1E293B; line-height: 1.6; padding: 40px; background-color: #FFFFFF; }
          .header { border-bottom: 3px solid #3311CC; padding-bottom: 20px; margin-bottom: 25px; display: flex; justify-content: space-between; align-items: flex-start; }
          .logo-area { font-size: 20px; font-weight: 800; color: #3311CC; text-transform: uppercase; letter-spacing: 1px; }
          .title { font-size: 22px; font-weight: bold; color: #0F172A; margin: 5px 0 0 0; }
          .subtitle { font-size: 14px; color: #64748B; margin-top: 5px; }
          .badge { background: #E0F2FE; color: #0284C7; padding: 6px 14px; border-radius: 20px; font-size: 12px; font-weight: bold; text-transform: uppercase; display: inline-block; }
          .info-box { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 12px; padding: 20px; margin-bottom: 25px; }
          .info-item { font-size: 13px; color: #475569; margin-bottom: 6px; }
          .info-item strong { color: #1E293B; }
          .section { background: #FFFFFF; border: 1px solid #E2E8F0; border-left: 4px solid #0EA5E9; border-radius: 8px; padding: 18px; margin-bottom: 18px; }
          .section.green { border-left-color: #10B981; }
          .section.indigo { border-left-color: #6366F1; }
          .section-title { font-size: 13px; font-weight: bold; text-transform: uppercase; color: #0EA5E9; margin-bottom: 8px; }
          .section.green .section-title { color: #10B981; }
          .section.indigo .section-title { color: #6366F1; }
          .section-text { font-size: 14px; color: #334155; white-space: pre-line; }
          .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #94A3B8; border-top: 1px solid #E2E8F0; padding-top: 15px; }
          @media print { body { padding: 20px; } .section { break-inside: avoid; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="logo-area">Plateforme S.O.A</div>
            <div class="title">Compte Rendu & Suivi</div>
            <div class="subtitle">Prestation : <strong>${offreTitre}</strong></div>
          </div>
          <div>
            <span class="badge">${((report.statut || '').toLowerCase() === 'valide' || (report.statut || '').toLowerCase() === 'validé') ? 'Validé & Signé' : 'Brouillon'}</span>
          </div>
        </div>

        <div class="info-box">
          <div>
            <div class="info-item"><strong>Séance :</strong> ${report.numero_seance || 'Séance n°1'}</div>
            <div class="info-item"><strong>Date de consultation :</strong> ${report.date_seance || '—'}</div>
            <div class="info-item"><strong>Lieu :</strong> ${offreLieu}</div>
          </div>
          <div>
            <div class="info-item"><strong>Intervenant / Praticien :</strong> ${praticienNom}</div>
            <div class="info-item"><strong>Date de rédaction :</strong> ${report.created_at ? new Date(report.created_at).toLocaleString('fr-FR') : '—'}</div>
          </div>
        </div>

        ${report.pratique ? `<div class="section"><div class="section-title">Pratique & Approche</div><div class="section-text">${report.pratique}</div></div>` : ''}
        ${report.perception_patient ? `<div class="section"><div class="section-title">Perception & État du Patient</div><div class="section-text">${report.perception_patient}</div></div>` : ''}
        ${report.resume_seance ? `<div class="section green"><div class="section-title">Résumé & Actes Réalisés</div><div class="section-text">${report.resume_seance}</div></div>` : ''}
        ${report.prevision_suite ? `<div class="section indigo"><div class="section-title">Plan de Soins & Suite</div><div class="section-text">${report.prevision_suite}</div></div>` : ''}

        <div class="footer">
          Plateforme S.O.A (Soins d'Onco-Accompagnement) • Document officiel généré le ${new Date().toLocaleDateString('fr-FR')}
        </div>

        <script>
          window.onload = function() { window.print(); window.onafterprint = function() { window.close(); } }
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (comptesRendus.length === 0) {
    return (
      <Box sx={{ p: 5, textAlign: 'center' }}>
        <AssignmentIcon sx={{ fontSize: 48, color: COLORS.muted, mb: 1, opacity: 0.5 }} />
        <Typography sx={{ color: COLORS.muted, fontWeight: 600 }}>
          Aucun compte rendu disponible pour ce patient.
        </Typography>
      </Box>
    );
  }

  const isValidStatut = (statut) => {
    const st = (statut || '').toLowerCase();
    return st === 'valide' || st === 'validé';
  };

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: COLORS.text }}>
        Comptes rendus des séances ({comptesRendus.length})
      </Typography>

      {/* TABLEAU DES COMPTES RENDUS */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid #E9EDF7',
          overflow: 'hidden',
        }}
      >
        <Table aria-label="tableau des comptes rendus">
          <TableHead sx={{ backgroundColor: '#F8FAFC' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, color: COLORS.text }}>Nom Réservation</TableCell>
              <TableCell sx={{ fontWeight: 800, color: COLORS.text }}>Date Réservation</TableCell>
              <TableCell sx={{ fontWeight: 800, color: COLORS.text }}>Date Compte Rendu</TableCell>
              <TableCell sx={{ fontWeight: 800, color: COLORS.text }}>Acteur</TableCell>
              <TableCell sx={{ fontWeight: 800, color: COLORS.text }}>Statut</TableCell>
              <TableCell align="center" sx={{ fontWeight: 800, color: COLORS.text }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {comptesRendus.map((cr) => {
              const nomReservation = cr.reservation?.offre?.info_offre?.titre_offre || cr.pratique || '—';
              const dateReservation = cr.reservation?.start_time_reservation
                ? new Date(cr.reservation.start_time_reservation).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
                : '—';
              const dateCompteRendu = cr.date_seance
                ? new Date(cr.date_seance).toLocaleDateString('fr-FR')
                : '—';
              const acteur = cr.user
                ? `${cr.user.prenom_user || ''} ${cr.user.nom_user || ''}`.trim()
                : 'Praticien';

              return (
                <TableRow key={cr.id_compte_rendu || cr.id_comptes_rendus || cr.id} hover>
                  <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>
                    {nomReservation}
                  </TableCell>
                  <TableCell sx={{ color: COLORS.text, whiteSpace: 'nowrap' }}>
                    {dateReservation}
                  </TableCell>
                  <TableCell sx={{ color: COLORS.text, whiteSpace: 'nowrap' }}>
                    {dateCompteRendu}
                  </TableCell>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 600 }}>
                    {acteur}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={isValidStatut(cr.statut) ? 'Validé' : (cr.statut || 'Brouillon')}
                      size="small"
                      sx={{
                        backgroundColor: isValidStatut(cr.statut) ? '#DCFCE7' : '#FFEDD5',
                        color: isValidStatut(cr.statut) ? '#15803D' : '#C2410C',
                        fontWeight: 700,
                        textTransform: 'capitalize',
                        borderRadius: '8px'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Voir le détail complet">
                      <IconButton
                        size="small"
                        onClick={() => setSelectedCr(cr)}
                        sx={{
                          backgroundColor: COLORS.primarySoft,
                          color: COLORS.primary,
                          '&:hover': { backgroundColor: COLORS.primary, color: '#FFF' },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* MODALE DÉTAIL DU COMPTE RENDU (Style identique à l'image 3) */}
      <Dialog
        open={Boolean(selectedCr)}
        onClose={() => setSelectedCr(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: '20px', p: 0, overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#FFFFFF',
            borderBottom: '1px solid #E2E8F0',
            py: 2.5,
            px: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: '#3311CC', width: 42, height: 42 }}>
              <AssignmentIcon sx={{ color: '#FFFFFF' }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>
                Compte Rendu Médical
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                {selectedCr?.numero_seance || 'Séance'} {selectedCr?.id_compte_rendu || selectedCr?.id_comptes_rendus ? `• Réf #${selectedCr.id_compte_rendu || selectedCr.id_comptes_rendus}` : ''}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              label={isValidStatut(selectedCr?.statut) ? 'Validé' : 'Brouillon'}
              size="small"
              sx={{
                bgcolor: isValidStatut(selectedCr?.statut) ? '#DCFCE7' : '#FFEDD5',
                color: isValidStatut(selectedCr?.statut) ? '#15803D' : '#C2410C',
                fontWeight: 700,
                borderRadius: '8px',
                px: 1,
              }}
            />
            <IconButton onClick={() => setSelectedCr(null)} size="small" sx={{ color: '#94A3B8' }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 3, bgcolor: '#FFFFFF' }}>
          {selectedCr && (
            <Stack spacing={2.5}>
              {/* En-tête Patient / Praticien */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Avatar sx={{ bgcolor: '#E0E7FF', color: '#4338CA', width: 40, height: 40 }}>
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Patient / Client
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B' }}>
                        {patient?.prenom_patient && patient?.nom_patient ? `${patient.prenom_patient} ${patient.nom_patient}` : (patient?.nom_patient || 'Client S.O.A')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        {patient?.email_patient || '—'}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Avatar sx={{ bgcolor: '#DCFCE7', color: '#15803D', width: 40, height: 40 }}>
                      <LocalHospitalIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Praticien
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B' }}>
                        {selectedCr.user ? `${selectedCr.user.prenom_user || ''} ${selectedCr.user.nom_user || ''}`.trim() : 'Praticien S.O.A'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        Soin : {selectedCr.reservation?.offre?.info_offre?.titre_offre || selectedCr.pratique || '—'}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* Date de séance */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: '#475569', px: 0.5 }}>
                <EventNoteIcon color="action" fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Date séance : {selectedCr.date_seance ? new Date(selectedCr.date_seance).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                </Typography>
              </Stack>

              {/* Blocs de contenu stylisés */}
              {selectedCr.perception_patient && (
                <Box sx={{ p: 2.5, bgcolor: '#F8FAFC', borderRadius: 3, border: '1px solid #E2E8F0', borderLeft: '4px solid #3311CC' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B', mb: 1 }}>
                    Perception du patient
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#334155', whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                    {selectedCr.perception_patient}
                  </Typography>
                </Box>
              )}

              {selectedCr.resume_seance && (
                <Box sx={{ p: 2.5, bgcolor: '#F8FAFC', borderRadius: 3, border: '1px solid #E2E8F0', borderLeft: '4px solid #0EA5E9' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B', mb: 1 }}>
                    Résumé de la séance
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#334155', whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                    {selectedCr.resume_seance}
                  </Typography>
                </Box>
              )}

              {selectedCr.prevision_suite && (
                <Box sx={{ p: 2.5, bgcolor: '#F8FAFC', borderRadius: 3, border: '1px solid #E2E8F0', borderLeft: '4px solid #10B981' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B', mb: 1 }}>
                    Prévision pour la suite
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#334155', whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                    {selectedCr.prevision_suite}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, bgcolor: '#FFFFFF', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => handleDownloadPDF(selectedCr)}
            sx={{
              borderColor: '#EF4444',
              color: '#EF4444',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { borderColor: '#DC2626', bgcolor: '#FEF2F2' },
            }}
          >
            Télécharger / Imprimer PDF
          </Button>

          <Button
            variant="contained"
            onClick={() => setSelectedCr(null)}
            sx={{
              bgcolor: '#0F172A',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              px: 4,
              '&:hover': { bgcolor: '#1E293B' },
            }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default function ViewPatientDialog({
  open,
  patientId,
  onClose,
  apiUrl,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [docToDelete, setDocToDelete] = useState(null);
  const [deletingDocId, setDeletingDocId] = useState(null);

  const { user } = useAuth();

  const rolePath = useMemo(() => {
    if (user?.role === 1 || user?.role === 2) {
      return 'admin';
    }
    if (user?.role === 4) {
      return 'praticien';
    }
    return 'intervenant';
  }, [user]);

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!open || !patientId) return;
    const fetchDetail = async () => {
      try {
        setLoading(true);
        setPatient(null);
        setDocuments([]);
        setActiveTab(0);
        const res = await axios.get(
          `${apiUrl}/${rolePath}/patients/${patientId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/json',
            },
          }
        );
        const data = res.data?.data || res.data?.patient || res.data;
        setPatient(data);
        setDocuments(data?.fiche_patient?.documents || []);
      } catch (err) {
        console.error('Erreur patient:', err?.response?.data || err);
        toast.error('Erreur lors du chargement de la fiche patient');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [open, patientId, apiUrl, token, rolePath]);

  const handleDeleteDocument = async (document) => {
    if (!document?.id_patient_document) return;
    try {
      setDeletingDocId(document.id_patient_document);
      await axios.delete(
        `${apiUrl}/${rolePath}/patients/documents/${document.id_patient_document}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );
      setDocuments((prev) =>
        prev.filter(
          (doc) => doc.id_patient_document !== document.id_patient_document
        )
      );
      toast.success('Document supprimé avec succès');
    } catch (err) {
      console.error('Erreur suppression document:', err?.response?.data || err);
      toast.error('Erreur lors de la suppression du document');
    } finally {
      setDeletingDocId(null);
      setDocToDelete(null);
    }
  };

  const handleClose = () => {
    setPatient(null);
    onClose();
  };

  const fiche = patient?.fiche_patient;
  const documentsCount = documents.length;
  const caregiversCount =
    fiche?.aidants?.length || patient?.aidants?.length || 0;

  const fullName = [patient?.prenom_patient, patient?.nom_patient]
    .filter(Boolean)
    .join(' ');

  const completion = useMemo(() => {
    if (!patient) return {};
    const identite = [
      patient.nom_patient,
      patient.prenom_patient,
      patient.email_patient,
      patient.telephone_patient,
      patient.date_naissance_patient,
      patient.lieu_residence_patient,
      patient.profession_patient,
    ];
    const sante = [
      patient.pathologie_patient,
      fiche?.deroule_cancer,
      fiche?.corps_medical,
      fiche?.etat_sante_aujourd_hui,
      fiche?.traitement_allopathique,
    ];
    const vie = [
      fiche?.enfance_famille_naissance,
      fiche?.vie_couple_famille,
      fiche?.vie_professionnelle,
      fiche?.alimentation,
      fiche?.sommeil,
    ];
    const accompagnement = [
      fiche?.attentes_soa,
      fiche?.situation_financiere,
      fiche?.autre_info,
    ];
    const ratio = (arr) =>
      arr.length
        ? Math.round((arr.filter(isFilled).length / arr.length) * 100)
        : 0;
    return {
      identite: ratio(identite),
      sante: ratio(sante),
      vie: ratio(vie),
      accompagnement: ratio(accompagnement),
    };
  }, [patient, fiche]);

  // Onglets (Comptes rendus à l'index 8)
  const tabs = [
    { label: "Vue d'ensemble", icon: <DashboardIcon fontSize="small" /> },
    { label: 'Compte client', icon: <AccountCircleIcon fontSize="small" /> },
    { label: 'Santé', icon: <FavoriteIcon fontSize="small" /> },
    { label: 'Parcours de vie', icon: <AutoStoriesIcon fontSize="small" /> },
    { label: 'Hygiène de vie', icon: <SpaIcon fontSize="small" /> },
    { label: 'Accompagnement SOA', icon: <VolunteerActivismIcon fontSize="small" /> },
    { label: `Aidants (${caregiversCount})`, icon: <SupervisorAccountIcon fontSize="small" /> },
    { label: 'Réservation', icon: <EventAvailableIcon fontSize="small" /> },
    { label: 'Comptes rendus', icon: <AssignmentIcon fontSize="small" /> },
    { label: `Documents (${documentsCount})`, icon: <AttachFileIcon fontSize="small" /> },
    { label: 'Dossier', icon: <AccessTimeIcon fontSize="small" /> },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        fullScreen={fullScreen}
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : '20px',
            overflow: 'hidden',
            height: fullScreen ? '100%' : '92vh',
            maxHeight: fullScreen ? '100%' : '92vh',
            boxShadow: '0 24px 80px rgba(43,54,116,0.18)',
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            background: 'linear-gradient(135deg, #F7F5FF 0%, #FFFFFF 70%)',
          }}
        >
          <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Avatar
                src={
                  patient?.photo_patient
                    ? `${apiUrl}/storage/${patient.photo_patient}`
                    : undefined
                }
                sx={{
                  width: 72,
                  height: 72,
                  backgroundColor: COLORS.primary,
                  fontSize: 28,
                  fontWeight: 800,
                  boxShadow: '0 8px 24px rgba(67,24,255,0.22)',
                }}
              >
                {!patient?.photo_patient && <PersonIcon fontSize="large" />}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h5"
                  sx={{ color: COLORS.text, fontWeight: 900, mb: 0.8 }}
                >
                  {loading ? 'Chargement…' : fullName || 'Patient'}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    icon={<FolderIcon sx={{ fontSize: 16 }} />}
                    size="small"
                    label={`Dossier #${patient?.code_patient || '—'}`}
                    sx={{
                      backgroundColor: COLORS.primarySoft,
                      color: COLORS.primary,
                      fontWeight: 700,
                    }}
                  />
                  {patient?.statut_pro_patient && (
                    <Chip
                      size="small"
                      label={patient.statut_pro_patient}
                      sx={{
                        backgroundColor: COLORS.successBg,
                        color: COLORS.success,
                        fontWeight: 700,
                        textTransform: 'capitalize',
                      }}
                    />
                  )}
                  {patient?.situation_familiale_patient && (
                    <Chip
                      size="small"
                      label={patient.situation_familiale_patient}
                      sx={{
                        backgroundColor: COLORS.warningBg,
                        color: COLORS.warning,
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Stack>
              </Box>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: COLORS.muted,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                    }}
                  >
                    ID Patient
                  </Typography>
                  <Typography
                    sx={{ color: COLORS.text, fontWeight: 800, fontSize: 18 }}
                  >
                    #{patient?.code_patient || '—'}
                  </Typography>
                </Box>
                <IconButton
                  onClick={handleClose}
                  size="small"
                  sx={{ color: COLORS.muted }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
          <Divider />
          {!loading && patient && (
            <Box
              sx={{ px: { xs: 2, md: 3 }, py: 1.5, backgroundColor: '#FFFFFF' }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneIcon sx={{ fontSize: 18, color: COLORS.primary }} />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {patient.telephone_patient || 'Non renseigné'}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <EmailIcon sx={{ fontSize: 18, color: COLORS.primary }} />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {patient.email_patient || 'Non renseigné'}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LocationOnIcon
                      sx={{ fontSize: 18, color: COLORS.primary }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {patient.lieu_residence_patient || 'Non renseigné'}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          )}
          <Divider />
          {!loading && patient && (
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                px: { xs: 1, md: 2 },
                backgroundColor: '#FFFFFF',
                minHeight: 52,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: 13.5,
                  minHeight: 52,
                  color: COLORS.muted,
                  gap: 0.7,
                },
                '& .Mui-selected': { color: `${COLORS.primary} !important` },
                '& .MuiTabs-indicator': {
                  backgroundColor: COLORS.primary,
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                },
              }}
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                  label={tab.label}
                />
              ))}
            </Tabs>
          )}
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, md: 3 }, backgroundColor: COLORS.bg }}>
          {loading ? (
            <Box
              sx={{
                minHeight: 450,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Stack spacing={2} alignItems="center">
                <CircularProgress size={44} thickness={4} />
                <Typography sx={{ color: COLORS.muted, fontWeight: 600 }}>
                  Chargement du dossier patient…
                </Typography>
              </Stack>
            </Box>
          ) : !patient ? (
            <Box
              sx={{
                minHeight: 300,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography sx={{ color: COLORS.muted }}>
                Aucune donnée disponible.
              </Typography>
            </Box>
          ) : (
            <>
              <TabPanel value={activeTab} index={0}>
                <OverviewTab patient={patient} />
              </TabPanel>
              <TabPanel value={activeTab} index={1}>
                <ClientAccountTab patient={patient} />
              </TabPanel>
              <TabPanel value={activeTab} index={2}>
                <HealthTab patient={patient} />
              </TabPanel>
              <TabPanel value={activeTab} index={3}>
                <LifePathTab patient={patient} />
              </TabPanel>
              <TabPanel value={activeTab} index={4}>
                <LifestyleTab patient={patient} />
              </TabPanel>
              <TabPanel value={activeTab} index={5}>
                <SoaTab patient={patient} />
              </TabPanel>
              <TabPanel value={activeTab} index={6}>
                <CaregiversTab patient={patient} />
              </TabPanel>
              <TabPanel value={activeTab} index={7}>
                <ReservationTab patient={patient} />
              </TabPanel>
              {/* Onglet Comptes rendus (Index 8) */}
              <TabPanel value={activeTab} index={8}>
                <ComptesRendusPatientTab
                  patient={patient}
                  apiUrl={apiUrl}
                  rolePath={rolePath}
                  token={token}
                />
              </TabPanel>
              <TabPanel value={activeTab} index={9}>
                <DocumentsTab
                  documents={documents}
                  apiUrl={apiUrl}
                  token={token}
                  onRequestDelete={setDocToDelete}
                  deletingDocId={deletingDocId}
                />
              </TabPanel>
              <TabPanel value={activeTab} index={10}>
                <MetaInfoTab patient={patient} />
              </TabPanel>
            </>
          )}
        </DialogContent>

        <Divider />
        <DialogActions sx={{ px: 3, py: 2, backgroundColor: '#FFFFFF' }}>
          <Typography variant="caption" sx={{ flex: 1, color: '#A3AED0' }}>
            Dossier patient #{patient?.id_patient_profile || '—'}
            {!loading && patient && completion.identite !== undefined && (
              <>
                {' '}
                · Complétude du dossier:{' '}
                {Math.round(
                  (completion.identite +
                    completion.sante +
                    completion.vie +
                    completion.accompagnement) /
                    4
                )}
                %
              </>
            )}
          </Typography>
          <Button
            onClick={handleClose}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              px: 3,
              backgroundColor: COLORS.primary,
              '&:hover': { backgroundColor: COLORS.primaryDark },
            }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modale de suppression de document */}
      <Dialog
        open={Boolean(docToDelete)}
        onClose={() => setDocToDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: COLORS.text }}>
          Supprimer ce document?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: COLORS.muted }}>
            Le document « {docToDelete?.nom_original || 'ce document'} » sera
            définitivement supprimé du dossier. Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDocToDelete(null)}
            disabled={Boolean(deletingDocId)}
            sx={{ textTransform: 'none', fontWeight: 700, color: COLORS.muted }}
          >
            Annuler
          </Button>
          <Button
            onClick={() => handleDeleteDocument(docToDelete)}
            variant="contained"
            disabled={Boolean(deletingDocId)}
            startIcon={
              deletingDocId ? (
                <CircularProgress
                  size={16}
                  thickness={5}
                  sx={{ color: '#FFF' }}
                />
              ) : null
            }
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              backgroundColor: '#E23D3D',
              '&:hover': { backgroundColor: '#C92F2F' },
            }}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}